<script setup lang="ts">
import { computed } from "vue";

export type PillColor =
  | "primary"
  | "gray"
  | "amber"
  | "green"
  | "emerald"
  | "blue";

export type PillVariant = "soft" | "solid";

type Props = {
  color?: PillColor;
  variant?: PillVariant;
  tag?: string;
};

const props = withDefaults(defineProps<Props>(), {
  color: "primary",
  variant: "soft",
  tag: "span",
});

const colorMap: Record<PillColor, Record<PillVariant, string>> = {
  primary: {
    soft: "bg-primary-50 dark:bg-primary-800 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-700",
    solid:
      "bg-primary-600 dark:bg-primary-500 text-white border border-primary-600 dark:border-primary-500",
  },
  gray: {
    soft: "bg-gray-100 dark:bg-primary-800 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-primary-700",
    solid:
      "bg-gray-600 dark:bg-gray-500 text-white border border-gray-600 dark:border-gray-500",
  },
  amber: {
    soft: "bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-700",
    solid:
      "bg-amber-500 dark:bg-amber-600 text-white border border-amber-500 dark:border-amber-600",
  },
  green: {
    soft: "bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-700",
    solid:
      "bg-green-600 dark:bg-green-500 text-white border border-green-600 dark:border-green-500",
  },
  emerald: {
    soft: "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700",
    solid:
      "bg-emerald-600 dark:bg-emerald-500 text-white border border-emerald-600 dark:border-emerald-500",
  },
  blue: {
    soft: "bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border border-blue-200 dark:border-blue-700",
    solid:
      "bg-blue-600 dark:bg-blue-500 text-white border border-blue-600 dark:border-blue-500",
  },
};

const classes = computed(
  () => colorMap[props.color as PillColor][props.variant as PillVariant]
);
</script>

<template>
  <component
    :is="tag"
    :class="[
      'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
      classes,
    ]"
  >
    <slot />
  </component>
</template>
